import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  Clock,
  DollarSign,
  Inbox,
  Megaphone,
  PiggyBank,
  Receipt,
  Sparkles,
  TrendingUp,
  UserPlus,
  Users,
} from "lucide-react";
import type { ComponentType, ReactNode } from "react";
import type { Paginated } from "@rn/contracts";
import { AgentDashboard } from "@/components/crm/AgentDashboard";
import { PageBody, PageHeader } from "@/components/crm/AppShell";
import { PeriodPicker } from "@/components/crm/PeriodPicker";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { Stat } from "@/components/ui/stat";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatCurrency, formatPhone, relativeTime } from "@/lib/crm";
import { usePeriod } from "@/lib/period";

type Summary = {
  total: number;
  unanswered: number;
  unassigned: number;
  qualified: number;
  converted: number;
  avgResponseSeconds: number | null;
  respondedUnder5min: number;
};

type Revenue = {
  revenue: number;
  cost: number;
  margin: number;
  today: number;
  paidCount: number;
  pendingCount: number;
  pendingValue: number;
  averageTicket: number;
};

type LeadRow = {
  id: string;
  source: string | null;
  createdAt: string;
  firstResponseAt: string | null;
  contact: { fullName: string | null; phoneNormalized: string | null };
  campaignName: string | null;
};

export const Route = createFileRoute("/_authenticated/painel")({
  component: PainelPage,
});

function PainelPage() {
  const { can } = useAuth();
  // Duas telas com o mesmo nome, e não uma com números escondidos: quem
  // atende precisa saber o que fazer agora, quem gere precisa saber quanto
  // entrou. Misturar as duas dá uma tela que não serve bem a nenhum dos dois.
  if (!can("admin", "manager")) return <PainelDoAtendente />;
  return <PainelDaGestao />;
}

function PainelDoAtendente() {
  return (
    <>
      <PageHeader title="Painel" description="O que precisa da sua atenção agora" />
      <AgentDashboard />
    </>
  );
}

/** Grade padrão dos números: 1 coluna no celular, 2 no tablet, 4 no desktop. */
const GRADE_KPI = "grid gap-4 sm:grid-cols-2 xl:grid-cols-4";

function PainelDaGestao() {
  // O período é o mesmo de todas as telas: quem escolhe "ontem" aqui e abre
  // os relatórios quer continuar vendo ontem. Só os números o seguem — as
  // listas de baixo são fila de trabalho, e lead esperando resposta não deixa
  // de esperar porque entrou antes do período escolhido.
  const { query, key } = usePeriod();

  // Agregação vem pronta do banco. A v1 puxava todos os leads de 30 dias e
  // somava em JavaScript — o que para de funcionar por volta de 10 mil linhas.
  const summary = useQuery({
    queryKey: ["leads", "summary", key],
    queryFn: () => api.get<Summary>("/leads/summary", query),
  });

  // A mesma resposta paginada da tela de leads: o `total` vira o contador do
  // cartão sem uma segunda consulta.
  const recent = useQuery({
    queryKey: ["leads", "recent"],
    queryFn: () => api.get<Paginated<LeadRow>>("/leads", { pageSize: 8 }),
  });

  // Receita conta SOMENTE venda paga. É a regra que impede o painel de
  // mostrar faturamento que ainda não entrou — e, por consequência, de inflar
  // qualquer ROAS calculado em cima dele.
  const revenue = useQuery({
    queryKey: ["sales", "revenue", key],
    queryFn: () => api.get<Revenue>("/sales/revenue", query),
  });

  const pending = useQuery({
    queryKey: ["leads", "unanswered"],
    queryFn: () => api.get<Paginated<LeadRow>>("/leads", { pageSize: 5, unanswered: true }),
  });

  // Receita sem custo de mídia ao lado é meia informação: faturamento alto com
  // anúncio caro é prejuízo, e é justamente o caso que passa despercebido.
  const midia = useQuery({
    queryKey: ["analytics", "overview", key],
    queryFn: () => api.get<Overview>("/analytics/overview", query),
  });

  const vendasPagas = revenue.data?.paidCount ?? 0;

  return (
    <>
      <PageHeader
        title="Painel"
        description="Receita, mídia e atendimento no período"
        action={<PeriodPicker />}
      />

      <PageBody className="space-y-6">
        <MediaBand overview={midia.data} loading={midia.isPending} />

        <Secao titulo="Receita">
          <div className={GRADE_KPI}>
            <Stat
              label="Receita paga"
              value={revenue.data ? formatCurrency(revenue.data.revenue) : undefined}
              loading={revenue.isLoading}
              icon={DollarSign}
              tone="success"
              hint={
                revenue.data && revenue.data.pendingValue > 0
                  ? `${formatCurrency(revenue.data.pendingValue)} a receber`
                  : "só o que já entrou"
              }
            />
            <Stat
              label="Margem"
              value={revenue.data ? formatCurrency(revenue.data.margin) : undefined}
              loading={revenue.isLoading}
              icon={TrendingUp}
              // Margem negativa é o serviço custando mais do que cobra — merece
              // a mesma cor de alerta do "Sobrou" da faixa de mídia.
              tone={revenue.data && revenue.data.margin < 0 ? "danger" : "default"}
              hint={
                revenue.data && revenue.data.revenue > 0
                  ? `${Math.round((revenue.data.margin / revenue.data.revenue) * 100)}% sobre a receita`
                  : undefined
              }
            />
            <Stat
              label="Ticket médio"
              value={revenue.data ? formatCurrency(revenue.data.averageTicket) : undefined}
              loading={revenue.isLoading}
              icon={Receipt}
              hint={
                revenue.data
                  ? `${vendasPagas} ${vendasPagas === 1 ? "venda paga" : "vendas pagas"}`
                  : undefined
              }
            />
            <Stat
              label="Vendido hoje"
              value={revenue.data ? formatCurrency(revenue.data.today) : undefined}
              loading={revenue.isLoading}
              icon={Sparkles}
              tone="primary"
            />
          </div>
        </Secao>

        <Secao titulo="Atendimento">
          <div className={GRADE_KPI}>
            <Stat
              label="Leads recebidos"
              value={summary.data?.total}
              loading={summary.isLoading}
              icon={Users}
              tone="primary"
              hint="no período"
            />
            <Stat
              label="Sem atendimento"
              value={summary.data?.unanswered}
              loading={summary.isLoading}
              icon={AlertTriangle}
              tone={summary.data?.unanswered ? "warning" : "default"}
              hint="esperando a primeira resposta"
            />
            <Stat
              label="Sem responsável"
              value={summary.data?.unassigned}
              loading={summary.isLoading}
              icon={UserPlus}
              hint="ninguém da equipe atribuído"
            />
            <Stat
              label="Tempo médio de resposta"
              value={formatDuration(summary.data?.avgResponseSeconds)}
              loading={summary.isLoading}
              icon={Clock}
              // Deixa explícito que o número desconta o fora do expediente — um
              // lead da madrugada não é culpa da equipe.
              hint="em horário comercial"
            />
          </div>
        </Secao>

        <div className="grid gap-6 lg:grid-cols-2">
          <ListaDeLeads
            titulo="Aguardando primeira resposta"
            descricao="Fila de agora, independente do período"
            total={pending.data?.pagination.total}
            loading={pending.isLoading}
            rows={pending.data?.data ?? []}
            espera
            vazio={{
              icon: CheckCircle2,
              title: "Nenhum lead esperando",
              description: "Bom sinal: todo lead novo já recebeu a primeira resposta.",
            }}
          />
          <ListaDeLeads
            titulo="Últimos leads"
            acao={
              <Button asChild variant="ghost" size="sm" className="-mr-2">
                <Link to="/leads">
                  Ver todos
                  <ArrowRight />
                </Link>
              </Button>
            }
            loading={recent.isLoading}
            rows={recent.data?.data ?? []}
            vazio={{
              icon: Inbox,
              title: "Nenhum lead ainda",
              description:
                "Assim que um contato chegar por WhatsApp, Instagram ou anúncio, ele aparece aqui.",
            }}
          />
        </div>
      </PageBody>
    </>
  );
}

/** Um grupo de números com rótulo pequeno em cima, para a tela não virar uma parede de cartões iguais. */
function Secao({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="font-sans text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {titulo}
      </h2>
      {children}
    </section>
  );
}

/**
 * Lista de leads em cartão.
 *
 * `espera` marca a lista de quem ainda não foi respondido: ali o tempo é o
 * dado que importa, então ele vira etiqueta de atenção em vez de texto
 * apagado à direita.
 */
function ListaDeLeads({
  titulo,
  descricao,
  total,
  acao,
  rows,
  loading,
  espera,
  vazio,
}: {
  titulo: string;
  descricao?: string;
  /** Total no servidor — pode ser maior do que as linhas mostradas. */
  total?: number;
  acao?: ReactNode;
  rows: LeadRow[];
  loading: boolean;
  espera?: boolean;
  vazio: { icon: ComponentType<{ className?: string }>; title: string; description?: string };
}) {
  return (
    <Card className="overflow-hidden">
      <CardHeader className="items-center pb-4">
        <div className="min-w-0">
          <CardTitle className="flex items-center gap-2">
            {titulo}
            {total !== undefined && total > 0 && (
              <Badge variant={espera ? "warning" : "muted"} className="tabular-nums">
                {total}
              </Badge>
            )}
          </CardTitle>
          {descricao && <CardDescription>{descricao}</CardDescription>}
        </div>
        {acao}
      </CardHeader>

      <div className="border-t">
        {loading ? (
          <LinhasCarregando />
        ) : rows.length === 0 ? (
          <EmptyState
            compact
            icon={vazio.icon}
            title={vazio.title}
            description={vazio.description}
          />
        ) : (
          <ul className="divide-y">
            {rows.map((lead) => (
              <li key={lead.id}>
                <Link
                  to="/leads/$leadId"
                  params={{ leadId: lead.id }}
                  className="flex items-center gap-3 px-5 py-3 transition-colors hover:bg-muted/40"
                >
                  <Avatar
                    name={lead.contact.fullName}
                    seed={lead.contact.phoneNormalized ?? lead.id}
                    size="md"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">
                      {lead.contact.fullName ?? "Sem nome"}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {formatPhone(lead.contact.phoneNormalized)}
                      {lead.campaignName ? ` · ${lead.campaignName}` : ""}
                    </p>
                  </div>
                  {espera ? (
                    <Badge variant="warning" className="tabular-nums">
                      <Clock />
                      {relativeTime(lead.createdAt)}
                    </Badge>
                  ) : (
                    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                      {relativeTime(lead.createdAt)}
                    </span>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}

/** Linhas com o mesmo tamanho das de verdade, para a lista não pular ao carregar. */
function LinhasCarregando({ linhas = 3 }: { linhas?: number }) {
  return (
    <div className="divide-y">
      {Array.from({ length: linhas }, (_, i) => (
        <div key={i} className="flex items-center gap-3 px-5 py-3">
          <Skeleton className="size-9 shrink-0 rounded-full" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-2/5" />
            <Skeleton className="h-3 w-3/5" />
          </div>
          <Skeleton className="h-3 w-12" />
        </div>
      ))}
    </div>
  );
}

function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return "—";
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}min`;
}

/** "1 lead" / "12 leads", com o número em pt-BR. */
function contar(n: number, um: string, varios: string): string {
  return `${n.toLocaleString("pt-BR")} ${n === 1 ? um : varios}`;
}

type Overview = {
  leads: { total: number };
  receita: { total: number; custo: number; vendas: number };
  midia: {
    investimento: number;
    cliques: number;
    impressoes: number;
    cpl: number | null;
    cac: number | null;
    roas: number | null;
  };
  resultado: { lucro: number; margemSobreReceita: number | null };
};

/**
 * Faixa de mídia do painel.
 *
 * Fica ACIMA da receita de propósito. A pergunta que a gestão faz ao abrir o
 * sistema não é "quanto entrou", é "sobrou?" — e receita sem o custo do
 * anúncio ao lado responde a pergunta errada com confiança.
 *
 * Some inteira quando não há investimento no período: uma faixa de zeros
 * ocupando o topo da tela ensina a ignorar aquele espaço, e é lá que o aviso
 * de ROAS abaixo de 1 vai aparecer quando importar.
 *
 * Enquanto carrega, os cinco cartões aparecem com o número em branco — é o
 * mesmo tamanho do resultado, então a tela não pula quando ele chega.
 */
function MediaBand({ overview, loading }: { overview?: Overview; loading: boolean }) {
  if (!loading && (!overview || overview.midia.investimento <= 0)) return null;

  const midia = overview?.midia;
  const resultado = overview?.resultado;
  const roas = midia?.roas ?? null;
  const noPrejuizo = roas !== null && roas < 1;
  const margem = resultado?.margemSobreReceita ?? null;

  return (
    <Secao titulo="Mídia paga">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
        <Stat
          label="Investido em mídia"
          icon={Megaphone}
          tone="primary"
          loading={loading}
          value={midia ? formatCurrency(midia.investimento) : undefined}
          hint={midia && midia.cliques > 0 ? contar(midia.cliques, "clique", "cliques") : undefined}
        />
        <Stat
          label="ROAS"
          icon={TrendingUp}
          tone={roas === null ? "default" : noPrejuizo ? "danger" : "success"}
          loading={loading}
          value={
            roas === null
              ? "—"
              : `${roas.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}x`
          }
          hint="receita paga ÷ investido"
        />
        <Stat
          label="Custo por lead"
          icon={Users}
          loading={loading}
          value={midia ? (midia.cpl === null ? "—" : formatCurrency(midia.cpl)) : undefined}
          hint={overview ? contar(overview.leads.total, "lead", "leads") : undefined}
        />
        <Stat
          label="Custo por venda"
          icon={Receipt}
          loading={loading}
          value={midia ? (midia.cac === null ? "—" : formatCurrency(midia.cac)) : undefined}
          hint={overview ? contar(overview.receita.vendas, "venda", "vendas") : undefined}
        />
        {/* Receita menos custo do serviço menos mídia. É o número que diz se
            a operação ganha dinheiro — nenhum dos três sozinho diz. */}
        <Stat
          label="Sobrou"
          icon={PiggyBank}
          tone={resultado ? (resultado.lucro < 0 ? "danger" : "success") : "default"}
          loading={loading}
          value={resultado ? formatCurrency(resultado.lucro) : undefined}
          hint={margem === null ? undefined : `${Math.round(margem * 100)}% da receita`}
        />
      </div>

      {noPrejuizo && (
        <div
          role="status"
          className="flex items-start gap-2.5 rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <p>
            Cada real investido está voltando {formatCurrency(roas)}. No período, a mídia está
            custando mais do que traz.
          </p>
        </div>
      )}
    </Secao>
  );
}
